import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";

const THEMES = ["water_purifier", "automotive", "furniture"];
const FONTS = ["Outfit", "Nunito", "Cabinet Grotesk", "IBM Plex Sans", "Anton", "Manrope", "Playfair Display", "Work Sans"];

const PRESETS = {
  water_purifier: { primary_color: "#0284C7", secondary_color: "#0369A1", accent_color: "#38BDF8", bg_color: "#F0F9FF", text_color: "#0C4A6E", heading_font: "Outfit", body_font: "Nunito" },
  automotive: { primary_color: "#DC2626", secondary_color: "#991B1B", accent_color: "#EF4444", bg_color: "#0A0A0A", text_color: "#FAFAFA", heading_font: "Anton", body_font: "Manrope" },
  furniture: { primary_color: "#78350F", secondary_color: "#92400E", accent_color: "#D97706", bg_color: "#FAFAF9", text_color: "#451A03", heading_font: "Playfair Display", body_font: "Work Sans" },
};

export default function TenantBranding() {
  const [profile, setProfile] = useState(null);
  const refresh = () => api.get("/tenant/profile").then(({ data }) => setProfile(data));
  useEffect(() => { refresh(); }, []);

  if (!profile) return <div className="p-8 text-slate-500">Loading...</div>;
  const b = profile.branding || {};

  const applyPreset = (theme) => {
    setProfile({ ...profile, branding: { ...b, ...PRESETS[theme], theme, logo_url: b.logo_url, hero_image_url: b.hero_image_url } });
  };

  const save = async () => {
    try {
      await api.put("/tenant/profile", { branding: profile.branding, name: profile.name, contact_email: profile.contact_email, contact_phone: profile.contact_phone, address: profile.address, gst_number: profile.gst_number, razorpay_key_id: profile.razorpay_key_id, razorpay_key_secret: profile.razorpay_key_secret, referral_bonus: profile.referral_bonus, points_per_rupee: profile.points_per_rupee, point_value_rupee: profile.point_value_rupee, default_commission_type: profile.default_commission_type, default_commission_value: profile.default_commission_value });
      toast.success("Saved");
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setB = (patch) => setProfile({ ...profile, branding: { ...b, ...patch } });

  return (
    <div className="p-8">
      <h1 className="font-display text-3xl font-bold tracking-tight mb-1" data-testid="tenant-branding-title">Branding & Profile</h1>
      <p className="text-slate-500 text-sm mb-6">Make your storefront feel like your own app.</p>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-2 border-slate-200 space-y-4">
          <h3 className="font-display font-semibold">Theme</h3>
          <div className="flex flex-wrap gap-2">
            {THEMES.map((t) => (
              <Button key={t} size="sm" variant={b.theme === t ? "default" : "outline"} onClick={() => applyPreset(t)} data-testid={`theme-${t}`}>{t.replace("_", " ")}</Button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <ColorRow label="Primary" v={b.primary_color} onChange={(v) => setB({ primary_color: v })} testId="color-primary" />
            <ColorRow label="Secondary" v={b.secondary_color} onChange={(v) => setB({ secondary_color: v })} />
            <ColorRow label="Accent" v={b.accent_color} onChange={(v) => setB({ accent_color: v })} />
            <ColorRow label="Background" v={b.bg_color} onChange={(v) => setB({ bg_color: v })} />
            <ColorRow label="Text" v={b.text_color} onChange={(v) => setB({ text_color: v })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Heading font</Label>
              <Select value={b.heading_font} onValueChange={(v) => setB({ heading_font: v })}>
                <SelectTrigger data-testid="font-heading"><SelectValue /></SelectTrigger>
                <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Body font</Label>
              <Select value={b.body_font} onValueChange={(v) => setB({ body_font: v })}>
                <SelectTrigger data-testid="font-body"><SelectValue /></SelectTrigger>
                <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <ImageUpload value={b.logo_url} onChange={(v) => setB({ logo_url: v })} kind="logo" label="Business logo" testId="branding-logo" aspect="aspect-square" />
            <ImageUpload value={b.hero_image_url} onChange={(v) => setB({ hero_image_url: v })} kind="banner" label="Hero / homepage banner" testId="branding-hero" aspect="aspect-video" />
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h3 className="font-display font-semibold mb-3">Business profile</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Business name</Label><Input value={profile.name || ""} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name-input" /></div>
              <div><Label>Contact email</Label><Input value={profile.contact_email || ""} onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })} /></div>
              <div><Label>Contact phone</Label><Input value={profile.contact_phone || ""} onChange={(e) => setProfile({ ...profile, contact_phone: e.target.value })} /></div>
              <div><Label>GST number</Label><Input value={profile.gst_number || ""} onChange={(e) => setProfile({ ...profile, gst_number: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Address</Label><Input value={profile.address || ""} onChange={(e) => setProfile({ ...profile, address: e.target.value })} /></div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h3 className="font-display font-semibold mb-1">Payments — Razorpay (Mock)</h3>
            <p className="text-xs text-slate-500 mb-3">Plug your Razorpay keys later. Mock payments are enabled for now.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Key ID</Label><Input value={profile.razorpay_key_id || ""} onChange={(e) => setProfile({ ...profile, razorpay_key_id: e.target.value })} placeholder="rzp_test_..." /></div>
              <div><Label>Key Secret</Label><Input value={profile.razorpay_key_secret || ""} onChange={(e) => setProfile({ ...profile, razorpay_key_secret: e.target.value })} type="password" /></div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h3 className="font-display font-semibold mb-3">Rewards & Marketplace defaults</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>Referral bonus (₹)</Label><Input type="number" value={profile.referral_bonus || 0} onChange={(e) => setProfile({ ...profile, referral_bonus: +e.target.value })} /></div>
              <div><Label>Points per ₹</Label><Input type="number" step="0.01" value={profile.points_per_rupee || 0} onChange={(e) => setProfile({ ...profile, points_per_rupee: +e.target.value })} /></div>
              <div><Label>1 point = ₹</Label><Input type="number" step="0.1" value={profile.point_value_rupee || 1} onChange={(e) => setProfile({ ...profile, point_value_rupee: +e.target.value })} /></div>
              <div><Label>Default commission type</Label>
                <Select value={profile.default_commission_type || "percent"} onValueChange={(v) => setProfile({ ...profile, default_commission_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">Percent</SelectItem><SelectItem value="flat">Flat ₹</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Default commission value</Label><Input type="number" value={profile.default_commission_value || 0} onChange={(e) => setProfile({ ...profile, default_commission_value: +e.target.value })} /></div>
            </div>
          </div>

          <Button onClick={save} className="w-full mt-4" data-testid="branding-save-btn">Save Changes</Button>
        </Card>

        <Card className="p-5 border-slate-200 sticky top-6 h-fit">
          <h3 className="font-display font-semibold mb-3">Live preview</h3>
          <div className="rounded-xl overflow-hidden border" style={{ borderColor: b.primary_color, background: b.bg_color, color: b.text_color, fontFamily: b.body_font }}>
            {b.hero_image_url && <img src={b.hero_image_url} alt="hero" className="w-full h-32 object-cover" />}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                {b.logo_url && <img src={b.logo_url} className="w-8 h-8 object-contain" alt="" />}
                <span style={{ fontFamily: b.heading_font, color: b.primary_color, fontWeight: 800 }}>{profile.name}</span>
              </div>
              <p className="text-sm">Premium rentals near you.</p>
              <button className="mt-3 px-4 py-2 rounded text-sm font-medium" style={{ background: b.primary_color, color: "#fff", fontFamily: b.body_font }}>Browse Products</button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function ColorRow({ label, v, onChange, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2 items-center">
        <input type="color" value={v || "#000000"} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded border border-slate-200 cursor-pointer" data-testid={testId} />
        <Input value={v || ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}
